import { useId, useState } from "react";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { usePermissions } from "@/hooks/use-permissions";
import { useToast } from "@/hooks/use-toast";
import { parseErrorMessage } from "@/lib/queryClient";
import { financialThresholdsSchema, type FinancialThresholds } from "@shared/member-financials";
import { useFinancialSettings } from "./use-member-financials";

export function FinancialThresholdSettings({ initialPropertyId = 0 }: { initialPropertyId?: number }) {
  const [open, setOpen] = useState(false);
  const [propertyId, setPropertyId] = useState(initialPropertyId);
  const { canEdit, isLoading } = usePermissions();
  const { toast } = useToast();
  const { query, mutation } = useFinancialSettings(propertyId, open);
  const id = useId();
  const readOnly = isLoading || !canEdit("members") || query.data?.canEdit !== true || query.isError;
  const pending = mutation.isPending;
  function changeScope(value: string) {
    mutation.reset();
    setPropertyId(Number(value));
  }
  function save(thresholds: FinancialThresholds | null) {
    if (readOnly || pending) return;
    mutation.mutate(thresholds, {
      onSuccess: () => toast({ title: thresholds ? "Financial thresholds saved" : "Account defaults restored" }),
      onError: error => toast({ title: "Could not update thresholds", description: parseErrorMessage(error, "Please try again."), variant: "destructive" }),
    });
  }
  return (
    <Dialog open={open} onOpenChange={value => {
      if (pending) return;
      if (value) { setPropertyId(initialPropertyId); mutation.reset(); }
      setOpen(value);
    }}>
      <Button variant="outline" size="sm" onClick={() => { setPropertyId(initialPropertyId); mutation.reset(); setOpen(true); }}>
        <Settings2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Financial thresholds
      </Button>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg"
        onInteractOutside={event => { if (pending) event.preventDefault(); }}
        onEscapeKeyDown={event => { if (pending) event.preventDefault(); }}
        onPointerDownOutside={event => { if (pending) event.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Financial thresholds</DialogTitle>
          <DialogDescription>Account defaults or a property override. Display and classification only; existing Collections settings are unchanged.</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Dry-run only. Messaging disabled.</strong>
          <p className="mt-1">Saving thresholds does not approve observations or enable any sends.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${id}-scope`}>Settings scope</Label>
          <select id={`${id}-scope`} value={propertyId} disabled={pending || query.isFetching}
            onChange={event => changeScope(event.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value={0}>Account defaults</option>
            {(query.data?.properties ?? []).map(property => <option key={property.id} value={property.id}>{property.name || `Property #${property.id}`}</option>)}
            {propertyId > 0 && !query.data?.properties.some(property => property.id === propertyId) && <option value={propertyId}>Selected property #{propertyId}</option>}
          </select>
        </div>
        {query.isPending ? <Skeleton className="h-40 w-full" aria-label="Loading financial settings" /> : query.isError ? (
          <p role="alert" className="text-sm text-red-700">{parseErrorMessage(query.error, "Settings could not be loaded. Please retry.")}</p>
        ) : query.data ? (
          <>
            <p className="text-xs text-gray-500">
              Effective source: {query.data.source}. Account defaults: negative at or below {query.data.account.negativeAtOrBelow.toFixed(2)}; positive at or above {query.data.account.positiveAtOrAbove.toFixed(2)}.
              {propertyId > 0 && !query.data.override && " This property inherits account defaults."}
            </p>
            {readOnly && <p role="status" className="text-sm text-gray-600">Read-only. Only an authorized account owner can edit financial thresholds.</p>}
            <ThresholdForm key={`${propertyId}-${query.dataUpdatedAt}`}
              value={query.data.effective} disabled={readOnly || pending || query.isFetching}
              pending={pending} onSave={save} />
            {propertyId > 0 && <Button variant="outline" disabled={readOnly || pending || query.isFetching || !query.data.override} onClick={() => save(null)}>
              Restore account inheritance
            </Button>}
          </>
        ) : null}
        {mutation.isError && <p role="alert" className="text-sm text-red-700">{parseErrorMessage(mutation.error, "Settings could not be saved. Please retry.")}</p>}
        <Button variant="outline" disabled={pending || query.isFetching} onClick={() => { mutation.reset(); void query.refetch(); }}>
          {query.isFetching ? "Reloading…" : "Reload settings"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ThresholdForm({ value, disabled, pending, onSave }: {
  value: FinancialThresholds; disabled: boolean; pending: boolean; onSave: (value: FinancialThresholds) => void;
}) {
  const [negative, setNegative] = useState(String(value.negativeAtOrBelow));
  const [positive, setPositive] = useState(String(value.positiveAtOrAbove));
  const id = useId();
  const parsed = financialThresholdsSchema.safeParse({ negativeAtOrBelow: Number(negative), positiveAtOrAbove: Number(positive) });
  const valid = negative.trim() !== "" && positive.trim() !== "" && parsed.success;
  return (
    <form className="space-y-4" onSubmit={event => {
      event.preventDefault();
      if (!disabled && valid && parsed.success) onSave(parsed.data);
    }}>
      <p id={`${id}-help`} className="text-sm text-gray-600">Credit-positive: positive means credit; negative means amount owed. The zero band is strictly between the two thresholds, not a claim of payment or a verified zero balance.</p>
      <div className="space-y-2">
        <Label htmlFor={`${id}-negative`}>Negative / owed at or below ($)</Label>
        <Input id={`${id}-negative`} type="number" step="0.01" min="-1000000" max="-0.01" required
          value={negative} disabled={disabled} aria-describedby={`${id}-help`} onChange={event => setNegative(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${id}-positive`}>Positive / credit at or above ($)</Label>
        <Input id={`${id}-positive`} type="number" step="0.01" min="0.01" max="1000000" required
          value={positive} disabled={disabled} aria-describedby={`${id}-help`} onChange={event => setPositive(event.target.value)} />
      </div>
      {!valid && <p role="status" className="text-xs text-red-700">Use cent increments: negative must be at most −0.01 and positive at least 0.01, within ±1,000,000.</p>}
      <Button type="submit" disabled={disabled || !valid}>{pending ? "Saving…" : "Save thresholds"}</Button>
    </form>
  );
}
